/**
 * AtsScoreModal — focused ATS score analysis modal.
 *
 * Shows ATS score for the current CV (no job offer needed).
 * Runs runAnalyze with the current CV text, displays score + strengths/improvements.
 */

import { BarChart2, CheckCircle2, AlertTriangle, TrendingUp, BrainCircuit } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { ScoreRing } from '../../ui/ScoreRing';
import { ContextualLoader } from '../../ui/ContextualLoader';
import { InfoTooltip } from '../../ui/InfoTooltip';
import type { AiAnalyzeStructured } from '../../../lib/ai-types';

const TOOLTIP_ATS = "Les ATS (Applicant Tracking Systems) sont des logiciels utilisés par 75% des recruteurs pour filtrer les CV avant lecture humaine. Ce score estime vos chances de passer le premier filtre.";
const TOOLTIP_KEYWORDS = "Ces termes apparaissent dans l'offre mais pas dans votre CV. Les ajouter naturellement améliore votre score.";
const TOOLTIP_ADAPT = "L'IA réécrit votre CV en intégrant les mots-clés de l'offre, sans dénaturer votre profil.";

interface AtsScoreModalProps {
  open: boolean;
  onClose: () => void;
  aiAnalysisStructured: AiAnalyzeStructured | null;
  isAiAnalyzing: boolean;
  onRunAnalyze: () => void;
  /** Optional: opens AdaptToJobModal after closing this one */
  onAdaptCV?: () => void;
}

export function AtsScoreModal({
  open,
  onClose,
  aiAnalysisStructured,
  isAiAnalyzing,
  onRunAnalyze,
  onAdaptCV,
}: AtsScoreModalProps) {
  const score = aiAnalysisStructured?.atsScore;

  const scoreColor =
    score === undefined ? 'text-white/60'
    : score >= 75 ? 'text-emerald-400'
    : score >= 50 ? 'text-amber-400'
    : 'text-red-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          Score ATS
          <InfoTooltip content={TOOLTIP_ATS} />
        </span>
      }
      size="lg"
    >
      <div className="space-y-5">
        <p className="text-sm text-white/60">
          {"L'IA analyse ton CV et lui attribue un score de compatibilité ATS avec des conseils d'amélioration."}
        </p>

        {!aiAnalysisStructured && !isAiAnalyzing ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="p-4 rounded-2xl bg-white/[0.04] border border-white/10">
              <BarChart2 className="size-10 text-white/30" />
            </div>
            <p className="text-sm text-white/50 text-center max-w-xs">
              {"Lance l'analyse pour voir le score de compatibilité ATS de ton CV."}
            </p>
            <button
              type="button"
              onClick={onRunAnalyze}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-600/20"
            >
              <BarChart2 className="size-4" />
              Analyser mon CV
            </button>
          </div>
        ) : isAiAnalyzing ? (
          <ContextualLoader
            isLoading={isAiAnalyzing}
            onRetry={onRunAnalyze}
            estimatedDuration="~15 secondes"
          />
        ) : aiAnalysisStructured ? (
          <div className="space-y-5">
            {/* Big score — animated ring */}
            <div className="flex items-center gap-6">
              <ScoreRing score={score} size={140} />
              <div className="space-y-1">
                <p className={`text-sm font-bold ${scoreColor}`}>
                  {score === undefined ? '–' : score >= 75 ? 'Excellent' : score >= 50 ? 'Correct' : 'À améliorer'}
                </p>
                <p className="text-xs text-white/50 max-w-xs">{aiAnalysisStructured.summary}</p>
              </div>
            </div>

            {/* Sub scores */}
            {(aiAnalysisStructured.structureScore !== undefined || aiAnalysisStructured.clarityScore !== undefined) && (
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Structure', value: aiAnalysisStructured.structureScore },
                  { label: 'Clarté', value: aiAnalysisStructured.clarityScore },
                  { label: 'Mots-clés', value: aiAnalysisStructured.keywordsScore, tooltip: TOOLTIP_KEYWORDS },
                  { label: 'Résultats chiffrés', value: aiAnalysisStructured.quantifiedResultsScore },
                ].filter(s => s.value !== undefined).map(({ label, value, tooltip }) => (
                  <div key={label} className="rounded-lg bg-white/[0.03] border border-white/[0.07] p-2.5">
                    <div className="flex items-center gap-1 text-[11px] text-white/40 mb-1">
                      {label}
                      {tooltip && <InfoTooltip content={tooltip} />}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400 transition-all"
                          style={{ width: `${value}%` }}
                        />
                      </div>
                      <span className="text-xs font-bold text-white/70 w-6 text-right">{value}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Strengths */}
            {aiAnalysisStructured.strengths?.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-400">
                  <CheckCircle2 className="size-3.5" /> Points forts
                </div>
                <ul className="space-y-1">
                  {aiAnalysisStructured.strengths.map((s, i) => (
                    <li key={i} className="text-xs text-white/70 flex gap-2">
                      <span className="text-emerald-500/60 shrink-0">•</span>{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Improvements */}
            {aiAnalysisStructured.improvements?.length > 0 && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs font-semibold text-amber-400">
                  <TrendingUp className="size-3.5" /> À améliorer
                </div>
                <ul className="space-y-1">
                  {aiAnalysisStructured.improvements.map((s, i) => (
                    <li key={i} className="text-xs text-white/70 flex gap-2">
                      <AlertTriangle className="size-3 text-amber-500/60 shrink-0 mt-0.5" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 4.3 — CTA "Améliorer mon CV" */}
            {onAdaptCV && (
              <div className="pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => { onClose(); setTimeout(onAdaptCV, 150); }}
                  className="flex items-center justify-center gap-2 w-full px-5 py-3 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
                  title={TOOLTIP_ADAPT}
                >
                  <BrainCircuit className="size-4" />
                  Améliorer mon CV pour une offre
                </button>
                <p className="text-center text-xs text-white/30 mt-2">
                  {"L'IA adapte le contenu aux mots-clés de l'offre — vous gardez le contrôle via le diff."}
                </p>
              </div>
            )}

            <button
              type="button"
              onClick={onRunAnalyze}
              disabled={isAiAnalyzing}
              className="text-xs text-white/30 hover:text-white/50 transition-colors"
            >
              Relancer l'analyse
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
